import React from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import SportsSoccerRounded from '@mui/icons-material/SportsSoccerRounded';
import ArrowCircleLeftRounded from '@mui/icons-material/ArrowCircleLeftRounded';
import ArrowCircleRightRounded from '@mui/icons-material/ArrowCircleRightRounded';
import DirectionsRunRounded from '@mui/icons-material/DirectionsRunRounded';

// ─── Events adapter ───────────────────────────────────────────────────────────

export function adaptEvents(events, homeTeamId) {
  return events.map(e => {
    const minute = e.time.extra != null
      ? `${e.time.regular}+${e.time.extra}`
      : String(e.time.regular);
    const ev = e.event;
    if (ev.sub) {
      const d = ev.sub.detail;
      return { minute, type: 'sub', team: d.teamId === homeTeamId ? 'home' : 'away', playerOn: d.playerIn.name, playerOff: d.playerOut.name };
    }
    if (ev.card) {
      const d = ev.card.detail;
      const type = d.type === 'secondYellow' ? 'yellowred' : d.type;
      return { minute, type, team: d.teamId === homeTeamId ? 'home' : 'away', player: d.player.name };
    }
    if (ev.goal) {
      const d = ev.goal.detail;
      return { minute, type: 'goal', team: d.teamId === homeTeamId ? 'home' : 'away', player: d.player.name, assist: d.assist?.name ?? null };
    }
    return null;
  }).filter(Boolean);
}

// ─── Card icons ───────────────────────────────────────────────────────────────

const YellowCard = () => (
  <Box component="span" sx={{ display: 'inline-block', width: 11, height: 16, bgcolor: '#f5c518', borderRadius: '2px' }} />
);
const RedCard = () => (
  <Box component="span" sx={{ display: 'inline-block', width: 11, height: 16, bgcolor: '#e53935', borderRadius: '2px' }} />
);
const YellowRedCard = () => (
  <Box component="span" sx={{ position: 'relative', display: 'inline-block', width: 14, height: 18 }}>
    <Box component="span" sx={{ position: 'absolute', left: 0, bottom: 0, width: 11, height: 16, bgcolor: '#f5c518', borderRadius: '2px', transform: 'rotate(-15deg)', transformOrigin: 'bottom right' }} />
    <Box component="span" sx={{ position: 'absolute', right: 0, bottom: 0, width: 11, height: 16, bgcolor: '#e53935', borderRadius: '2px' }} />
  </Box>
);

// ─── Event icons ──────────────────────────────────────────────────────────────

function SubIcon({ team }) {
  const inward = team === 'home';
  const iconSx = { fontSize: 16 };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      {inward
        ? <ArrowCircleRightRounded sx={{ ...iconSx, color: '#4caf50' }} />
        : <ArrowCircleLeftRounded sx={{ ...iconSx, color: '#4caf50' }} />}
      {inward
        ? <ArrowCircleLeftRounded sx={{ ...iconSx, color: '#e53935' }} />
        : <ArrowCircleRightRounded sx={{ ...iconSx, color: '#e53935' }} />}
    </Box>
  );
}

function EventIcon({ type, team }) {
  if (type === 'goal') return <SportsSoccerRounded sx={{ width: 16, height: 16, color: '#fff' }} />;
  if (type === 'yellow') return <YellowCard />;
  if (type === 'red') return <RedCard />;
  if (type === 'yellowred') return <YellowRedCard />;
  if (type === 'sub') return <SubIcon team={team} />;
  return null;
}

// ─── Match events card ────────────────────────────────────────────────────────

export default function MatchEventsCard({ events }) {
  return (
    <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <DirectionsRunRounded sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Match Events
        </Typography>
      </Box>
      <Divider sx={{ mb: 1.5, borderColor: 'divider' }} />
      {events.map((event, i) => {
        const isHome = event.team === 'home';
        const isSub = event.type === 'sub';
        return (
          <Box
            key={i}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: isHome ? 'flex-start' : 'flex-end', py: 0.75, borderBottom: i < events.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            {isHome ? (
              <>
                <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 36, mr: 1 }}>
                  {event.minute}'
                </Typography>
                {isSub ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ArrowCircleRightRounded sx={{ fontSize: 16, color: '#4caf50' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>{event.playerOn}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ArrowCircleLeftRounded sx={{ fontSize: 16, color: '#e53935' }} />
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>{event.playerOff}</Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <EventIcon type={event.type} team={event.team} />
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>{event.player}</Typography>
                      {event.assist && (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>{event.assist}</Typography>
                      )}
                    </Box>
                  </>
                )}
              </>
            ) : (
              <>
                {isSub ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>{event.playerOn}</Typography>
                      <ArrowCircleLeftRounded sx={{ fontSize: 16, color: '#4caf50' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>{event.playerOff}</Typography>
                      <ArrowCircleRightRounded sx={{ fontSize: 16, color: '#e53935' }} />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mr: 1, textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>{event.player}</Typography>
                    {event.assist && (
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>{event.assist}</Typography>
                    )}
                  </Box>
                )}
                {!isSub && <EventIcon type={event.type} team={event.team} />}
                <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 36, ml: 1, textAlign: 'right' }}>
                  {event.minute}'
                </Typography>
              </>
            )}
          </Box>
        );
      })}
    </Paper>
  );
}
